<div class="relative block h-full">
  <app-header>
    <div primary-slot>
      <h3 class="text-xl font-medium text-grey-blue">Create Customer</h3>
      <p class="text-xs font-normal text-grey-mid">Review and complete the captured lead details</p>
    </div>
    <button
      additional-slot
      id="close-lead-customer-create-modal"
      class="btn-primary m-0 flex w-min items-center justify-center p-1"
      [disabled]="(loadingState$ | async) === 'loading'"
      (click)="closeModal()"
    >
      <ion-icon class="size-5" name="close-outline"></ion-icon>
    </button>
  </app-header>

  <div class="h-full overflow-y-scroll bg-grey-light-concrete px-4 pb-60 pt-3" [formGroup]="basicDetailsForm">
    <div
      class="flex items-start gap-2 rounded border border-blue-bright/30 bg-blue-bright/10 p-3 text-xs text-grey-blue font-normal mb-2"
    >
      <ion-icon name="information-circle" class="mt-0.5 size-4 shrink-0 text-blue-bright"></ion-icon>
      <span>These details came from the lead. Check them before creating the customer record.</span>
    </div>
    <div class="space-y-3">
      <section class="rounded bg-white p-3 shadow">
        <div
          class="form-input group relative w-full rounded border-grey-concrete transition-all delay-100"
          [ngClass]="{
            'p-2 pb-6 bg-white border-grey-concrete hover:shadow-inner hover:bg-grey-light-concrete/30 hover:scale-95 -ml-2': true,
            'border-2 border-dashed lg:border-white lg:hover:border-grey-concrete':
              basicDetailsForm.controls['parent_group_id'].value,
            'border-solid border shadow scale-95 mb-2 ml-0': !basicDetailsForm.controls['parent_group_id'].value,
            'has-error !border-bright-crimson/75 scale-95':
              basicDetailsForm.controls['parent_group_id'].invalid &&
              basicDetailsForm.controls['parent_group_id'].touched,
          }"
        >
          @if (
            basicDetailsForm.controls['parent_group_id'].hasError('required') &&
            basicDetailsForm.controls['parent_group_id'].touched
          ) {
            <label
              ><ion-icon name="alert-circle" class="text-base"></ion-icon>
              <div class="error">Parent is required</div></label
            >
          } @else {
            <label>Parent</label>
          }
          @if (basicDetailsForm.controls['parent_group_id'].value) {
            <app-group-display
              [groupId]="basicDetailsForm.controls['parent_group_id'].value"
              [groupName]="parentGroupName"
              [enableLink]="false"
            ></app-group-display>
          } @else {
            <span class="font-medium text-empty-field">unassigned</span>
          }
          <div
            class="absolute bottom-0 right-2 flex w-full items-end justify-end transition-opacity"
            [ngClass]="{
              'lg:opacity-0 lg:group-hover:opacity-100': basicDetailsForm.controls['parent_group_id'].value,
            }"
          >
            <button
              type="button"
              class="my-2 p-0.5 md:p-2 btn-primary flex"
              (click)="parentSelector.presentPopover($event)"
            >
              <ion-icon
                name="{{ basicDetailsForm.controls['parent_group_id'].value ? 'swap-horizontal' : 'add' }}"
                class="size-4"
              ></ion-icon>
              @if (!basicDetailsForm.controls['parent_group_id'].value) {
                <span class="ml-2 block text-xs">Select</span>
              }
            </button>
          </div>
        </div>
        <app-group-selector-container
          #parentSelector
          title="Select Parent"
          [groupFilters]="{ group_type: ['partner', 'distributor'] }"
          (selectedGroup)="selectParent($any($event))"
          presentation="popover"
        ></app-group-selector-container>

        <div class="form-input">
          <label class="text-xs font-normal">Entity Type</label>
          <div class="flex flex-wrap gap-2">
            <label
              class="flex cursor-pointer items-center rounded border border-grey-concrete px-3 py-2 text-sm font-normal text-black transition peer-checked:border-turquoise-mid peer-checked:ring-1 peer-checked:ring-primary hover:bg-primary/10"
              [class.border-turquoise-mid]="basicDetailsForm.controls['group_entity_type'].value === 'person'"
            >
              <input class="peer sr-only" type="radio" value="person" formControlName="group_entity_type" />
              <ion-icon
                [name]="
                  basicDetailsForm.controls['group_entity_type'].value === 'person'
                    ? 'checkmark-circle'
                    : 'ellipse-outline'
                "
                class="mr-2 size-5 text-turquoise-mid"
              ></ion-icon>
              Person
            </label>
            <label
              class="flex cursor-pointer items-center rounded border border-grey-concrete px-3 py-2 text-sm font-normal text-black transition peer-checked:border-turquoise-mid peer-checked:ring-1 peer-checked:ring-primary hover:bg-primary/10"
              [class.border-turquoise-mid]="basicDetailsForm.controls['group_entity_type'].value === 'company'"
            >
              <input class="peer sr-only" type="radio" value="company" formControlName="group_entity_type" />
              <ion-icon
                [name]="
                  basicDetailsForm.controls['group_entity_type'].value === 'company'
                    ? 'checkmark-circle'
                    : 'ellipse-outline'
                "
                class="mr-2 size-5 text-turquoise-mid"
              ></ion-icon>
              Company
            </label>
          </div>
        </div>

        <div
          class="form-input"
          [ngClass]="{
            'has-error': basicDetailsForm.controls['name'].invalid && basicDetailsForm.controls['name'].touched,
          }"
        >
          @if (basicDetailsForm.controls['name'].hasError('required') && basicDetailsForm.controls['name'].touched) {
            <label
              ><ion-icon name="alert-circle" class="text-base"></ion-icon>
              <div class="error">Name is required</div></label
            >
          } @else if (
            basicDetailsForm.controls['name'].hasError('pattern') && basicDetailsForm.controls['name'].touched
          ) {
            <label
              ><ion-icon name="alert-circle" class="text-base"></ion-icon>
              <div class="error">Name contains invalid characters</div></label
            >
          } @else {
            <label for="lead-customer-name">
              {{
                basicDetailsForm.controls['group_entity_type'].value === 'company' ? 'Company Name' : 'Customer Name'
              }}
            </label>
          }
          <input id="lead-customer-name" type="text" formControlName="name" autocomplete="organization" />
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)]">
          <div class="form-input">
            <label for="lead-customer-country">Country</label>

            <select id="lead-customer-country" formControlName="country_alpha2_code">
              @for (country of countries$ | async; track country.attributes.alpha2_code) {
                <option [value]="country.attributes.alpha2_code">
                  {{ country.attributes.alpha2_code | emojiFlag }} {{ country.attributes.name }}
                </option>
              }
            </select>
          </div>

          <div
            class="form-input"
            [ngClass]="{
              'has-error':
                basicDetailsForm.controls['vat_number'].invalid && basicDetailsForm.controls['vat_number'].touched,
            }"
          >
            <label for="lead-customer-vat-number">VAT Number</label>
            <input
              id="lead-customer-vat-number"
              type="text"
              class="text-sm"
              formControlName="vat_number"
              autocomplete="off"
            />
          </div>
        </div>

        @if (basicDetailsForm.controls['group_entity_type'].value === 'company') {
          <div
            class="form-input"
            [ngClass]="{
              'has-error':
                basicDetailsForm.controls['company_registration_number'].invalid &&
                basicDetailsForm.controls['company_registration_number'].touched,
            }"
          >
            @if (
              basicDetailsForm.controls['company_registration_number'].hasError('required') &&
              basicDetailsForm.controls['company_registration_number'].touched
            ) {
              <label>
                <ion-icon name="alert-circle" class="text-base"></ion-icon>
                <div class="error">Registration number is required unless exempt</div>
              </label>
            } @else {
              <label for="lead-customer-registration-number">Registration Number</label>
            }
            <div class="grid grid-cols-[2fr_140px]">
              <input
                id="lead-customer-registration-number"
                type="text"
                class="rounded-r-none border-r-0"
                formControlName="company_registration_number"
                autocomplete="off"
                [class.cursor-not-allowed]="basicDetailsForm.controls['crn_exempt'].value"
              />
              <div
                class="form-suffix flex h-10 items-center justify-center rounded rounded-l-none border border-grey-concrete bg-grey-light-concrete p-2 text-sm"
              >
                <ion-checkbox label-placement="end" class="-mt-1 p-0 text-sm" formControlName="crn_exempt">
                  Not available
                </ion-checkbox>
              </div>
            </div>
          </div>
        }
      </section>

      @if (isDirectCustomer) {
        <app-address-details
          class="block mb-4"
          [selectedCountry]="selectedCountry$ | async"
          [isExpanded]="true"
          [formOptional]="false"
          [addressDetailsForm]="addressDetailsForm"
          [formTitle]="'Customer Address'"
          [tradingEntity]="lead.attributes.trading_entity"
          [groupType]="'customer'"
          [context]="'leads'"
        ></app-address-details>
      }

      <section
        class="contact-role-theme flex min-w-0 flex-col rounded-lg border border-[rgb(var(--role-rgb)/var(--role-border-alpha))] bg-[linear-gradient(135deg,rgb(var(--role-rgb)/var(--role-selected-alpha))_0%,rgb(255_255_255)_55%,rgb(255_255_255)_100%)] p-3 shadow-sm"
        data-contact-role-color="turquoise-mid"
        [formGroup]="contactForm"
      >
        <div class="flex items-center gap-1 mb-2">
          <div
            class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-[rgb(var(--role-rgb)/0.15)] text-[var(--role-color)]"
          >
            <ion-icon name="call-outline" class="size-5"></ion-icon>
          </div>
          <div>
            <h4 class="m-0 text-xs font-medium leading-snug text-grey-blue">Primary Contact</h4>
            <p class="m-0 text-xxs text-grey-mid">This person will be linked to the new customer.</p>
          </div>
        </div>

        @if (basicDetailsForm.controls['group_entity_type'].value === 'company') {
          <div
            class="form-input"
            [ngClass]="{ 'has-error': contactForm.controls['name'].invalid && contactForm.controls['name'].touched }"
          >
            @if (contactForm.controls['name'].hasError('required') && contactForm.controls['name'].touched) {
              <label
                ><ion-icon name="alert-circle" class="text-base"></ion-icon>
                <div class="error">Contact name is required</div></label
              >
            } @else {
              <label for="lead-customer-contact-name">Contact Name</label>
            }
            <input
              id="lead-customer-contact-name"
              type="text"
              class="bg-white"
              formControlName="name"
              autocomplete="name"
            />
          </div>
        }

        <div
          class="form-input"
          [ngClass]="{ 'has-error': contactForm.controls['email'].invalid && contactForm.controls['email'].touched }"
        >
          @if (contactForm.controls['email'].hasError('required') && contactForm.controls['email'].touched) {
            <label
              ><ion-icon name="alert-circle" class="text-base"></ion-icon>
              <div class="error">Email is required</div></label
            >
          } @else if (contactForm.controls['email'].hasError('email') && contactForm.controls['email'].touched) {
            <label
              ><ion-icon name="alert-circle" class="text-base"></ion-icon>
              <div class="error">Enter a valid email address</div></label
            >
          } @else {
            <label for="lead-customer-contact-email">Email Address</label>
          }
          <input
            id="lead-customer-contact-email"
            type="email"
            class="bg-white"
            formControlName="email"
            autocomplete="email"
          />
        </div>

        <div class="form-input">
          <label for="lead-customer-contact-telephone">Telephone Number</label>
          <div class="flex rounded-md shadow-sm">
            <span class="form-prefix flex items-center gap-1 text-sm">
              <span class="text-base">{{ (selectedCountry$ | async).attributes.alpha2_code | emojiFlag }}</span>
              <span>+{{ (selectedCountry$ | async).attributes.dial_code }}</span>
            </span>
            <input
              id="lead-customer-contact-telephone"
              type="tel"
              class="rounded-none rounded-r-md bg-white"
              formControlName="telephone_number"
              autocomplete="tel"
              [class.rounded-l-md]="!(selectedCountry$ | async)"
            />
          </div>
        </div>
      </section>
    </div>
  </div>

  <div
    class="fixed bottom-0 flex w-full flex-row gap-2 border-t-2 border-grey-concrete bg-white/50 p-3 backdrop-blur-sm"
  >
    <button
      id="create-lead-customer"
      type="button"
      class="btn-primary text-sm"
      [disabled]="(loadingState$ | async) === 'loading'"
      (click)="saveCustomer()"
    >
      Create Customer
    </button>
    <button
      type="button"
      class="btn-default text-sm"
      [disabled]="(loadingState$ | async) === 'loading'"
      (click)="closeModal()"
    >
      Cancel
    </button>
  </div>
</div>
